import { createContext } from "preact"
import { useCallback, useContext, useEffect, useMemo, useState } from "preact/hooks"
import type { ComponentChildren } from "preact"
import { useNostoAppState } from "@nosto/search-js/preact/hooks"
import { useConfig } from "@nosto/search-js/preact/common"
import { defaultSize, styleFamilyCatalogMaxRawTotal, withCategoryMetadata } from "@/config"
import { dedupeByStyleFamily } from "@/utils/dedupeByStyleFamily"
import { fetchAllSearchHits } from "@/utils/fetchAllSearchHits"
import { fetchAvailability } from "@/utils/fetchAvailability"
import { searchQuerySignature } from "@/utils/searchQuerySignature"
import { getCurrentUrlState } from "@/mapping/url/getCurrentUrlState"
import { updateUrl } from "@/mapping/url/updateUrl"
import type { Product } from "@/types"
import { isRenderableProductHit } from "@/utils/isRenderableProductHit"
import { readInGridConfig } from "@/utils/ingridConfig"
import { computeInGridSlotCount, totalPagesWithInGrid } from "@/utils/paginationWithInGrid"

export type StyleFamilyCatalogStatus = "idle" | "loading" | "ready" | "fallback" | "error"

export type StyleFamilyCatalogContextValue = {
  status: StyleFamilyCatalogStatus
  dedupedHits: Product[] | null
  /**
   * Renderable hits *before* style-family dedupe — every variant that survives
   * `isRenderableProductHit`. Consumers that need per-family availability
   * (e.g. facet counts that should not jump when the user selects a value)
   * read this; consumers that need the visible-card set read `dedupedHits`.
   */
  renderableHits: Product[] | null
  rawTotal: number
  urlPage: number
  goToPage: (page: number) => void
  resetUrlPage: () => void
}

export const StyleFamilyCatalogContext = createContext<StyleFamilyCatalogContextValue | null>(null)

export function useStyleFamilyCatalog() {
  return useContext(StyleFamilyCatalogContext)
}

export function StyleFamilyCatalogProvider({ children }: { children: ComponentChildren }) {
  const { pageType } = useConfig()
  const { query, response, loading, initialized } = useNostoAppState(state => ({
    initialized: state.initialized,
    loading: state.loading,
    query: state.query,
    response: state.response
  }))

  const pageSize = useNostoAppState(state => state.query?.products?.size ?? defaultSize)
  const rawTotal = response?.products?.total ?? 0
  const signature = useMemo(() => searchQuerySignature(query), [query])

  const [urlPage, setUrlPageState] = useState(() => getCurrentUrlState().page ?? 1)
  const [catalogStatus, setCatalogStatus] = useState<StyleFamilyCatalogStatus>("idle")
  const [dedupedHits, setDedupedHits] = useState<Product[] | null>(null)
  const [renderableHits, setRenderableHits] = useState<Product[] | null>(null)

  const goToPage = useCallback((page: number) => {
    const p = Math.max(1, page)
    const state = getCurrentUrlState()
    updateUrl({ ...state, page: p > 1 ? p : undefined })
    setUrlPageState(p)
  }, [])

  const resetUrlPage = useCallback(() => {
    setUrlPageState(1)
    const state = getCurrentUrlState()
    if (state.page) {
      updateUrl({ ...state, page: undefined })
    }
  }, [])

  useEffect(() => {
    const onPop = () => setUrlPageState(getCurrentUrlState().page ?? 1)
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])

  useEffect(() => {
    if (!initialized) {
      return
    }

    // Nosto is fetching for the new query. Mark the catalog as no-longer-ready
    // *immediately* so `Products` keeps showing skeletons instead of briefly
    // rendering the previous filter's products between `loading: true -> false`
    // and the catalog's own re-fetch kicking off:
    // skeleton -> stale products -> skeleton -> new products flash).
    if (loading) {
      setCatalogStatus(prev => (prev === "ready" ? "loading" : prev))
      setDedupedHits(null)
      setRenderableHits(null)
      return
    }

    if (rawTotal <= 0) {
      setCatalogStatus("idle")
      setDedupedHits(null)
      setRenderableHits(null)
      return
    }

    if (rawTotal > styleFamilyCatalogMaxRawTotal) {
      setCatalogStatus("fallback")
      setDedupedHits(null)
      setRenderableHits(null)
      return
    }

    let cancelled = false
    setCatalogStatus("loading")
    setDedupedHits(null)
    setRenderableHits(null)
    ;(async () => {
      try {
        const fetchQuery = pageType === "category" ? withCategoryMetadata(query) : query
        const rawHits = await fetchAllSearchHits(fetchQuery, rawTotal)
        if (cancelled) {
          return
        }
        if (rawHits.length === 0 && rawTotal > 0) {
          setDedupedHits(null)
          setRenderableHits(null)
          setCatalogStatus("fallback")
          return
        }
        const filteredRenderable = rawHits.filter(isRenderableProductHit)
        const dedupedFromNosto = dedupeByStyleFamily(filteredRenderable)

        // Nosto's `available/saleable` flags are market-unaware. In en-ca /
        // fr-ca some products are unpublished or fully out of stock for that
        // market, so the `nosto-dickies-product-card` section returns its
        // empty-card stub. Pre-filter against Shopify's per-market
        // `product.available` so the visible grid stays uniform.
        //
        // The availability map is keyed by `productId` (locale-stable Shopify
        // ID), not by `handle` — Liquid renders translated handles per locale
        // while Nosto always returns the primary-locale handle, so a
        // handle-keyed map would miss every hit on `/fr-ca/`.
        const idsToCheck = new Map<string, Product>()
        for (const product of dedupedFromNosto) {
          const id = product.productId != null ? String(product.productId) : null
          if (id != null && !idsToCheck.has(id)) {
            idsToCheck.set(id, product)
          }
        }
        const availability = await fetchAvailability(Array.from(idsToCheck.values()))
        if (cancelled) {
          return
        }

        const isMarketAvailable = (p: Product) => {
          const id = p.productId != null ? String(p.productId) : null
          return id != null && (availability.get(id) ?? true)
        }

        const marketRenderable = filteredRenderable.filter(isMarketAvailable)
        const marketDeduped = dedupedFromNosto.filter(isMarketAvailable)

        setRenderableHits(marketRenderable)
        setDedupedHits(marketDeduped)
        setCatalogStatus("ready")
      } catch (error) {
        if (!cancelled) {
          setDedupedHits(null)
          setRenderableHits(null)
          setCatalogStatus("error")
        }
        console.error(error)
      }
    })()

    return () => {
      cancelled = true
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` content-addresses `query` */
  }, [initialized, loading, pageType, signature, rawTotal])

  useEffect(() => {
    if (catalogStatus !== "ready" || !dedupedHits || dedupedHits.length === 0) {
      return
    }
    // Slot-aware totalPages so a slot-induced extra final page isn't clamped
    // off when the in-grid tiles push the catalog past a clean page boundary.
    const slots = computeInGridSlotCount(readInGridConfig())
    const totalPages = Math.max(1, totalPagesWithInGrid(dedupedHits.length, pageSize, slots))
    const url = getCurrentUrlState()
    const page = url.page ?? 1
    if (page > totalPages) {
      const nextPage = totalPages
      updateUrl({ ...url, page: nextPage > 1 ? nextPage : undefined })
      setUrlPageState(nextPage)
    }
  }, [catalogStatus, dedupedHits, pageSize])

  const value = useMemo<StyleFamilyCatalogContextValue>(
    () => ({
      status: catalogStatus,
      dedupedHits,
      renderableHits,
      rawTotal,
      urlPage,
      goToPage,
      resetUrlPage
    }),
    [catalogStatus, dedupedHits, renderableHits, rawTotal, urlPage, goToPage, resetUrlPage]
  )

  return <StyleFamilyCatalogContext.Provider value={value}>{children}</StyleFamilyCatalogContext.Provider>
}
